import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { TutorChat } from "../components/TutorChat";
import {
  GraduationCap,
  BookOpen,
  PlayCircle,
  BarChart3,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Bot,
  Clock,
  FileDown,
  Briefcase,
  CalendarClock,
  UserCheck,
  Rocket,
  TrendingUp,
  MessageCircleQuestion,
  Crown,
  Star,
  Award,
  Users
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function LandingPage() {
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({ courses: 0, students: 0 });
  const [loading, setLoading] = useState(true);

  const availableCourses = courses.filter((c) => !c.coming_soon);
  const upcomingCourses = courses.filter((c) => c.coming_soon);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/courses`);
        setCourses(response.data.courses || []);
        setStats(response.data.stats || { courses: 0, students: 0 });
      } catch (error) {
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPublicData();
  }, []);

  const features = [
    {
      icon: Bot,
      title: "Tutor Virtual con IA",
      description: "Tu mentor disponible 24/7: resuelve dudas sobre consultoría, freelancing, captación de clientes o precios, y te orienta en tu formación paso a paso.",
      big: true
    },
    {
      icon: MessageCircleQuestion,
      title: "Tutorías 1 a 1 (VIP)",
      description: "Videollamadas o llamadas uno a uno con el equipo del centro. Servicio exclusivo para alumnos VIP: te confirmamos tu cita en menos de 72 horas.",
      vip: true
    },
    {
      icon: Briefcase,
      title: "Prácticas en Nuestra Consultoría",
      description: "Da el salto al mundo real: realiza prácticas en nuestra propia consultoría y trabaja en proyectos con clientes reales.",
      highlight: true
    },
    {
      icon: PlayCircle,
      title: "Vídeos Formativos",
      description: "Clases prácticas de alta calidad, disponibles cuando quieras y desde cualquier dispositivo."
    },
    {
      icon: FileDown,
      title: "Material Descargable",
      description: "Plantillas, guías y recursos en PDF listos para aplicar directamente en tu negocio."
    },
    {
      icon: BarChart3,
      title: "Seguimiento de Progreso",
      description: "Progreso por módulos y estadísticas detalladas para que avances con foco y sin perder el rumbo."
    }
  ];

  const steps = [
    {
      icon: UserCheck,
      title: "Recibe tu acceso",
      description: "El centro crea tu cuenta de alumno y te entrega tus credenciales personales de acceso al campus."
    },
    {
      icon: TrendingUp,
      title: "Fórmate a tu ritmo",
      description: "Aprende con vídeos, recursos prácticos y el apoyo del Tutor Virtual, cuando y donde quieras."
    },
    {
      icon: Rocket,
      title: "Impulsa tu negocio",
      description: "Aplica lo aprendido, consigue clientes y avanza hacia tu libertad financiera."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A1128] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-[#0A1128]" />
              </div>
              <div>
                <h1 className="font-bold text-white text-base sm:text-lg leading-tight tracking-tight">
                  CENTRO FORMATIVO URCAL
                </h1>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] hidden sm:block">
                  Campus Virtual
                </p>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <a href="#campus" className="text-sm text-slate-300 hover:text-white font-medium transition-colors">
                El Campus
              </a>
              <a href="#formacion" className="text-sm text-slate-300 hover:text-white font-medium transition-colors">
                Formación
              </a>
              <a href="#metodologia" className="text-sm text-slate-300 hover:text-white font-medium transition-colors">
                Metodología
              </a>
            </nav>

            <Link to="/login">
              <Button
                className="gold-gradient text-[#0A1128] font-semibold hover:opacity-90 btn-active shadow-[0_0_15px_rgba(212,175,55,0.3)]"
                data-testid="header-login-btn"
              >
                Acceder al Campus
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1741096931892-a786386813aa?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/5 rounded-full blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
                <Badge className="mb-8 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-1.5 text-xs tracking-widest uppercase" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Sparkles className="w-3.5 h-3.5 mr-2" />
                  Formación para consultores y freelancers
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={1}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-8"
              >
                Construye tu
                <br />
                <span className="gold-gradient-text italic">libertad financiera.</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={2}
                className="text-base md:text-lg text-slate-300 max-w-xl mb-10 leading-relaxed"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                El campus virtual del Centro Formativo URCAL: formación práctica para consultores
                y freelancers, con vídeos, material descargable y un Tutor Virtual con IA
                disponible 24/7 para impulsar tu negocio.
              </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={3}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/login">
                  <Button
                    size="lg"
                    className="gold-gradient text-[#0A1128] font-bold px-10 h-13 text-base hover:opacity-90 btn-active shadow-[0_0_25px_rgba(212,175,55,0.35)]"
                    data-testid="hero-login-btn"
                  >
                    Acceder al Campus
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#formacion">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white font-semibold px-10 h-13 text-base btn-active backdrop-blur"
                    data-testid="hero-courses-btn"
                  >
                    Ver Formación
                  </Button>
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={4}
                className="mt-16 flex gap-10 sm:gap-16"
                data-testid="hero-stats"
              >
                <div>
                  <p className="text-3xl sm:text-4xl font-bold gold-gradient-text">{stats.courses}+</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Cursos
                  </p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold gold-gradient-text">{stats.students}+</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Alumnos
                  </p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold gold-gradient-text">24/7</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Tutor IA
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Hero image */}
            <motion.div
              className="lg:col-span-5 relative hidden lg:block"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative">
                <div className="absolute -inset-4 gold-gradient opacity-20 blur-2xl rounded-[2.5rem]" />
                <div className="relative rounded-[2rem] overflow-hidden border border-[#D4AF37]/30 shadow-2xl">
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/dd73e03a-b9ba-458e-a8df-3bcb223cc525/images/0107cee39d9d044992e93c7314ab009421a5e03fafb13c0939d2b49c616b06cd.png"
                    alt="Consultor profesional trabajando"
                    className="w-full h-[520px] object-cover"
                    fetchPriority="high"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent" />
                </div>

                <motion.div
                  className="absolute -left-10 top-14 glass-panel rounded-xl p-4 flex items-center gap-3"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <p className="text-sm font-semibold text-white">Tutor Virtual IA</p>
                    <p className="text-xs text-slate-400">Disponible 24/7</p>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute -right-6 bottom-16 glass-panel rounded-xl p-4 flex items-center gap-3"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-cyan-300" />
                  </div>
                  <div style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <p className="text-sm font-semibold text-white">Tu negocio</p>
                    <p className="text-xs text-slate-400">Creciendo cada día</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Animated stats strip */}
      <section className="relative py-12 border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Users,   value: "+74",   label: "Consultores formados" },
              { icon: Award,   value: "72h",   label: "Confirmación de tutorías VIP" },
              { icon: Star,    value: "4.9/5", label: "Satisfacción media" },
              { icon: Clock,   value: "24/7",  label: "Tutor IA disponible" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="flex flex-col items-center gap-2"
                data-testid={`stat-${i}`}
              >
                <stat.icon className="w-6 h-6 text-[#D4AF37]" />
                <p className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "Playfair Display, serif" }}>{stat.value}</p>
                <p className="text-xs uppercase tracking-widest text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee ticker */}
      <div className="relative py-6 border-b border-white/5 bg-black/30 overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap items-center text-slate-500 text-sm uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} className="flex items-center gap-16 shrink-0">
              {[
                "Consultoría",
                "•",
                "Freelancing",
                "•",
                "Ventas B2B",
                "•",
                "Escala tu negocio",
                "•",
                "Libertad financiera",
                "•",
                "Mentoría VIP",
                "•",
                "Prácticas reales",
                "•",
                "URCAL Campus",
              ].map((word, idx) => (
                <span key={`${dup}-${idx}`} className={word === "•" ? "text-[#D4AF37]" : ""}>{word}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bento Features */}
      <section id="campus" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mb-16 max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              El Campus
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              Todo lo que necesitas para crecer
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={index}
                whileHover={{ y: -6 }}
                className={`glass-card rounded-2xl p-8 card-hover relative overflow-hidden ${feature.big ? "md:col-span-2 md:row-span-1" : ""} ${feature.highlight ? "!border-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.1)]" : ""} ${feature.vip ? "!border-[#D4AF37]/50 shadow-[0_0_40px_rgba(212,175,55,0.15)]" : ""}`}
                data-testid={`feature-card-${index}`}
              >
                {feature.vip && (
                  <div className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[10px] font-bold uppercase tracking-widest text-[#D4AF37] flex items-center gap-1">
                    <Crown className="w-3 h-3" /> VIP
                  </div>
                )}
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/25 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses */}
      <section id="formacion" className="py-24 relative">
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Formación
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                Nuestros cursos
              </h2>
            </div>
            <p className="text-sm text-slate-400 max-w-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Formación práctica para consultores y freelancers. Accede con tu cuenta de alumno para empezar.
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : availableCourses.length === 0 ? (
            <div className="text-center py-16 glass-card rounded-2xl">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Próximamente nueva oferta formativa
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {availableCourses.map((course, index) => (
                <motion.div
                  key={course.id}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  custom={index % 3}
                  className="glass-card rounded-2xl overflow-hidden card-hover group"
                  data-testid={`course-card-${index}`}
                >
                  <div className="h-52 relative overflow-hidden">
                    {course.cover_image ? (
                      <img
                        src={course.cover_image}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1a2647] to-[#0A1128] flex items-center justify-center">
                        <BookOpen className="w-16 h-16 text-white/10" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/30 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <Badge className="badge-gold border-0 text-xs uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {course.category || "General"}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-5 right-5">
                      <h3 className="text-xl font-bold text-white line-clamp-2">
                        {course.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <p className="text-sm text-slate-400 mb-6 line-clamp-2 min-h-[40px] leading-relaxed">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between border-t border-white/10 pt-5">
                      <span className="flex items-center gap-1.5 text-xs text-slate-400">
                        <PlayCircle className="w-4 h-4 text-[#D4AF37]" />
                        {course.content_count || 0} contenidos
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-[#D4AF37] font-semibold">
                        <ShieldCheck className="w-4 h-4" />
                        Alumnos
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Próximos Cursos */}
          {!loading && upcomingCourses.length > 0 && (
            <div className="mt-20" data-testid="upcoming-courses-section">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                className="mb-10 flex items-center gap-4"
              >
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
                  <CalendarClock className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                    Próximos cursos
                  </h3>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Muy pronto en el campus
                  </p>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {upcomingCourses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                    custom={index % 3}
                    className="relative rounded-2xl overflow-hidden border border-dashed border-[#D4AF37]/40 bg-white/[0.02] card-hover"
                    data-testid={`upcoming-course-card-${index}`}
                  >
                    <div className="h-44 relative overflow-hidden">
                      {course.cover_image ? (
                        <img
                          src={course.cover_image}
                          alt={course.title}
                          className="w-full h-full object-cover opacity-50 saturate-50"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1a2647] to-[#0A1128] flex items-center justify-center">
                          <CalendarClock className="w-14 h-14 text-white/10" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/40 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-[#0A1128]/80 border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] animate-pulse" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Próximamente
                        </Badge>
                      </div>
                      <div className="absolute bottom-4 left-5 right-5">
                        <h3 className="text-lg font-bold text-white line-clamp-2">
                          {course.title}
                        </h3>
                      </div>
                    </div>
                    <div className="p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <p className="text-sm text-slate-400 mb-5 line-clamp-2 leading-relaxed">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-2 border-t border-white/10 pt-4 text-xs text-[#D4AF37] font-semibold">
                        <Clock className="w-4 h-4" />
                        Disponible muy pronto
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Metodología */}
      <section id="metodologia" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mb-16 max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Metodología
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              Así funciona el campus
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={index}
                className="relative p-8 rounded-2xl border border-white/8 bg-white/[0.02]"
              >
                <span className="absolute top-6 right-8 text-6xl font-bold text-white/5 select-none">
                  0{index + 1}
                </span>
                <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center mb-6">
                  <step.icon className="w-6 h-6 text-[#0A1128]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 p-12 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-[#111827]" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-yellow-500/10 blur-[100px] rounded-full" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
                ¿Ya eres alumno de URCAL?
              </h2>
              <p className="text-slate-400 mb-10 max-w-xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Entra en el campus con tus credenciales y continúa construyendo tu negocio donde lo dejaste.
                Tu Tutor Virtual te está esperando.
              </p>
              <Link to="/login">
                <Button
                  size="lg"
                  className="gold-gradient text-[#0A1128] font-bold px-10 h-13 hover:opacity-90 btn-active shadow-[0_0_25px_rgba(212,175,55,0.35)]"
                  data-testid="cta-login-btn"
                >
                  Acceder al Campus
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-[#0A1128]" />
              </div>
              <div>
                <span className="font-bold text-white text-lg block leading-tight">
                  CENTRO FORMATIVO URCAL
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                  Formación para consultores y freelancers
                </span>
              </div>
            </div>

            <Link
              to="/login"
              className="text-sm text-white hover:text-[#D4AF37] transition-colors font-medium"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="footer-login-link"
            >
              Acceso Alumnos →
            </Link>
          </div>

          <div className="border-t border-white/5 mt-10 pt-6 text-center text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <p>© {new Date().getFullYear()} Centro Formativo URCAL. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Tutor Virtual público */}
      <TutorChat publicMode />
    </div>
  );
}
